import { useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, Volume2, GraduationCap } from "lucide-react";

const LETTERS = ["A", "B", "C", "D"];
const blankQ = () => ({ q: "", options: ["", "", "", ""], correct: 0 });

const VideoQuizStudio = ({ manualId, initial }) => {
  const [enabled, setEnabled] = useState(!!initial?.enabled);
  const [voiceover, setVoiceover] = useState(!!initial?.voiceover);
  const [questions, setQuestions] = useState(() => {
    const qs = (initial?.questions || []).map((q) => ({
      q: q.q || "",
      options: [...(q.options || []), "", "", "", ""].slice(0, 4),
      correct: q.correct ?? 0,
    }));
    return qs.length ? qs : [];
  });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const persist = useCallback(async (next) => {
    const payload = {
      enabled: next.enabled,
      voiceover: next.voiceover,
      questions: (next.questions || []).filter((q) => q.q.trim() && q.options.filter((o) => o.trim()).length >= 2),
    };
    setSaving(true);
    try {
      await api.post(`/manuals/${manualId}/quiz`, payload);
    } catch { /* silent — retried on next change */ }
    finally { setSaving(false); }
  }, [manualId]);

  const commit = (patch) => {
    const next = { enabled, voiceover, questions, ...patch };
    setEnabled(next.enabled); setVoiceover(next.voiceover); setQuestions(next.questions);
    persist(next);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/manuals/${manualId}/quiz/generate`);
      const qs = (data?.questions || []).map((q) => ({ q: q.q, options: [...q.options, "", "", "", ""].slice(0, 4), correct: q.correct ?? 0 }));
      if (qs.length) { commit({ enabled: true, questions: qs }); toast.success("Drafted 4 quiz questions ✨ — edit any of them"); }
      else toast.error("Couldn't draft questions right now — try again or add your own");
    } catch { toast.error("Couldn't draft questions right now"); }
    finally { setBusy(false); }
  };

  const setQ = (i, field, val) => {
    const next = questions.map((q, j) => (j === i ? { ...q, [field]: val } : q));
    setQuestions(next);
  };
  const setOpt = (i, oi, val) => {
    const next = questions.map((q, j) => (j === i ? { ...q, options: q.options.map((o, k) => (k === oi ? val : o)) } : q));
    setQuestions(next);
  };
  const save = () => { commit({}); toast.success("Quiz saved"); };

  const rowStyle = (on) => ({ borderColor: on ? "#1e3aaa" : "#2a2838", background: on ? "rgba(30,58,170,.14)" : "transparent" });

  return (
    <div data-testid="video-quiz-studio">
      <p className="text-[13px] text-[#a8a4ba] mb-3 text-center">
        A <b className="text-white">Pop Quiz</b> plays at the very end — 4 challenging A–D questions, then the answers reveal. Great for classrooms, and it keeps viewers watching to the finish.
      </p>

      <label data-testid="quiz-enable-row" className="flex items-center gap-2.5 mb-2 cursor-pointer select-none rounded-2xl border-2 px-4 py-3" style={rowStyle(enabled)}>
        <input type="checkbox" data-testid="quiz-enable-checkbox" checked={enabled} onChange={(e) => commit({ enabled: e.target.checked })} className="w-5 h-5 rounded shrink-0" style={{ accentColor: "#1e3aaa" }} />
        <span className="leading-tight">
          <span className="flex items-center gap-2 font-extrabold text-[14px] text-white"><GraduationCap size={16} className="text-[#5b9bff]" /> Add an end-of-video Quiz</span>
          <span className="block text-[11px] font-semibold text-[#a8a4ba]">Off by default. Flip on to test comprehension right before your end card.</span>
        </span>
      </label>

      {enabled && (
        <>
          <label data-testid="quiz-voiceover-row" className="flex items-center gap-2.5 mb-3 cursor-pointer select-none rounded-2xl border-2 px-4 py-2.5" style={rowStyle(voiceover)}>
            <input type="checkbox" data-testid="quiz-voiceover-checkbox" checked={voiceover} onChange={(e) => commit({ voiceover: e.target.checked })} className="w-5 h-5 rounded shrink-0" style={{ accentColor: "#4f7746" }} />
            <span className="flex items-center gap-2 font-bold text-[13px] text-white"><Volume2 size={15} className="text-[#8fbf82]" /> Read questions &amp; answers aloud</span>
          </label>

          <button data-testid="quiz-generate-btn" onClick={generate} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 text-sm font-extrabold rounded-xl px-4 py-2.5 mb-4 text-white bg-[#1e3aaa] hover:bg-[#183089] disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Writing 4 questions…</> : <><Sparkles size={16} /> Generate 4 questions from my video</>}
          </button>

          {questions.length === 0 && (
            <p className="text-center text-[12px] text-[#7c7791] mb-3">No questions yet — tap Generate, or add your own below.</p>
          )}

          <div className="space-y-4">
            {questions.map((q, i) => (
              <div key={i} className="rounded-2xl border border-[#2a2838] bg-[#101019] p-3.5" data-testid={`quiz-q-${i}`}>
                <label className="text-[11px] font-bold text-[#a8a4ba] uppercase tracking-wide">Question {i + 1}</label>
                <input
                  data-testid={`quiz-q-input-${i}`}
                  value={q.q}
                  onChange={(e) => setQ(i, "q", e.target.value)}
                  onBlur={save}
                  maxLength={160}
                  placeholder="Type the question…"
                  className="w-full mt-1 mb-2.5 rounded-xl px-3 py-2 text-sm text-white bg-black outline-none"
                  style={{ border: "2px solid #1e3aaa" }}
                />
                <div className="space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`quiz-correct-${i}-${oi}`}
                        onClick={() => { setQ(i, "correct", oi); setTimeout(save, 0); }}
                        title="Mark correct"
                        className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold"
                        style={q.correct === oi ? { background: "#2e9e4f", color: "#fff", border: "2px solid #78e696" } : { background: "#000", color: "#a9d79b", border: "2px solid #5d8a52" }}
                      >
                        {q.correct === oi ? <Check size={13} /> : LETTERS[oi]}
                      </button>
                      <input
                        data-testid={`quiz-opt-${i}-${oi}`}
                        value={opt}
                        onChange={(e) => setOpt(i, oi, e.target.value)}
                        onBlur={save}
                        maxLength={80}
                        placeholder={`Option ${LETTERS[oi]}`}
                        className="flex-1 rounded-lg px-3 py-1.5 text-sm text-white bg-black outline-none"
                        style={{ border: "1.5px solid #2a2838" }}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[#7c7791] mt-1.5">Tap the letter to mark the correct answer.</p>
              </div>
            ))}
          </div>

          <p className="text-center text-[11px] text-[#7c7791] mt-3">{saving ? "Saving…" : "Changes save automatically"}</p>
        </>
      )}
    </div>
  );
};

export default VideoQuizStudio;
